<!--
  Transaction Table Component
  Displays transactions in a sortable, filterable data table
  @component TransactionTable
-->
<template>
  <div class="transaction-table">
    <q-table
      :rows="transactions"
      :columns="columns"
      :loading="loading"
      :pagination="pagination"
      :selected="selected"
      :selection="selectable ? 'multiple' : 'none'"
      row-key="id"
      flat
      bordered
      :dense="dense"
      @update:selected="$emit('update:selected', $event)"
      @row-click="handleRowClick">
      <!-- Loading slot -->
      <template #loading>
        <q-inner-loading
          showing
          color="primary" />
      </template>

      <!-- Top slot for table controls -->
      <template #top>
        <div class="row items-center justify-between full-width">
          <div class="col-auto">
            <h6 class="q-ma-none">
              {{ t('composites.TransactionProcessingDashboard.table.transactions_title') }}
            </h6>
            <p class="text-caption text-grey-6 q-ma-none">
              {{ t('composites.TransactionProcessingDashboard.table.showing_results', { count: transactions.length }) }}
            </p>
          </div>
          <div class="col-auto">
            <q-btn-group>
              <q-btn
                :icon="symRoundedRefresh"
                :label="t('common.refresh')"
                color="primary"
                outline
                size="sm"
                @click="$emit('refresh')" />
              <q-btn
                v-if="selectable && selected.length > 0"
                :icon="symRoundedMoreVert"
                :label="t('composites.TransactionProcessingDashboard.table.bulk_actions')"
                color="secondary"
                outline
                size="sm">
                <q-menu>
                  <q-list style="min-width: 200px">
                    <q-item
                      clickable
                      @click="$emit('bulk-retry', selected)">
                      <q-item-section avatar>
                        <q-icon :name="symRoundedReplay" />
                      </q-item-section>
                      <q-item-section>{{ t('composites.TransactionProcessingDashboard.table.retry_selected') }}</q-item-section>
                    </q-item>
                    <q-item
                      clickable
                      @click="$emit('bulk-export', selected)">
                      <q-item-section avatar>
                        <q-icon :name="symRoundedFileDownloadOff" />
                      </q-item-section>
                      <q-item-section>{{ t('composites.TransactionProcessingDashboard.table.export_selected') }}</q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </q-btn-group>
          </div>
        </div>
      </template>

      <!-- Status column -->
      <template #body-cell-status="props">
        <q-td :props="props">
          <q-badge
            :color="getStatusColor(props.value)"
            :label="getStatusLabel(props.value)"
            class="q-px-sm" />
        </q-td>
      </template>

      <!-- Amount column -->
      <template #body-cell-total_amount="props">
        <q-td :props="props">
          <div class="text-weight-medium">
            {{ formatCurrency(props.row.amount_total, props.row.currency) }}
          </div>
        </q-td>
      </template>

      <!-- Customer column -->
      <template #body-cell-customer="props">
        <q-td :props="props">
          <div class="column">
            <div class="text-weight-medium">
              {{ props.row.receiver_billing?.legal_name || t('common.not_available') }}
            </div>
            <div class="text-caption text-grey-6">
              {{ (props.row.billingEmails?.length ? props.row.billingEmails.join(', ') : props.row.billingEmail) || t('common.no_email') }}
            </div>
          </div>
        </q-td>
      </template>

      <!-- Date column -->
      <template #body-cell-transaction_date="props">
        <q-td :props="props">
          <div class="column">
            <div>{{ formatDate(props.value) }}</div>
            <div class="text-caption text-grey-6">
              {{ formatRelativeTime(props.value) }}
            </div>
          </div>
        </q-td>
      </template>

      <!-- Transmissions column -->
      <template #body-cell-transmissions="props">
        <q-td :props="props">
          <div class="row q-gutter-xs">
            <q-chip
              v-for="transmission in props.row.transmissions?.slice(0, 3) || []"
              :key="transmission.id"
              :color="getTransmissionStatusColor(transmission.status)"
              :icon="getTransmissionStatusIcon(transmission.status)"
              size="sm"
              text-color="white">
              {{ getTransmissionTypeLabel(transmission.type) }}
            </q-chip>
            <q-chip
              v-if="(props.row.transmissions?.length || 0) > 3"
              color="grey"
              size="sm"
              text-color="white">
              +{{ (props.row.transmissions?.length || 0) - 3 }}
            </q-chip>
          </div>
        </q-td>
      </template>

      <!-- Actions column -->
      <template #body-cell-actions="props">
        <q-td :props="props">
          <q-btn-group>
            <q-btn
              :icon="symRoundedVisibility"
              color="primary"
              size="sm"
              flat
              @click="$emit('view-details', props.row)">
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.table.view_details') }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="hasFailedTransmissions(props.row)"
              :icon="symRoundedReplay"
              color="warning"
              size="sm"
              flat
              @click="$emit('retry-transmissions', props.row)">
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.table.retry_failed') }}</q-tooltip>
            </q-btn>
            <q-btn
              :icon="symRoundedMoreVert"
              color="grey"
              size="sm"
              flat>
              <q-tooltip>{{ t('composites.TransactionProcessingDashboard.table.more_actions') }}</q-tooltip>
              <q-menu>
                <q-list style="min-width: 180px">
                  <q-item
                    clickable
                    @click="$emit('edit-transaction', props.row)">
                    <q-item-section avatar>
                      <q-icon :name="symRoundedEdit" />
                    </q-item-section>
                    <q-item-section>{{ t('composites.TransactionProcessingDashboard.table.edit') }}</q-item-section>
                  </q-item>
                  <q-item
                    clickable
                    @click="$emit('duplicate-transaction', props.row)">
                    <q-item-section avatar>
                      <q-icon :name="symRoundedContentCopy" />
                    </q-item-section>
                    <q-item-section>{{ t('composites.TransactionProcessingDashboard.table.duplicate') }}</q-item-section>
                  </q-item>
                  <q-separator />
                  <q-item
                    clickable
                    @click="$emit('cancel-transaction', props.row)">
                    <q-item-section avatar>
                      <q-icon :name="symRoundedClose" />
                    </q-item-section>
                    <q-item-section>{{ t('composites.TransactionProcessingDashboard.table.cancel') }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </q-btn-group>
        </q-td>
      </template>

      <!-- No data message -->
      <template #no-data>
        <div class="full-width row flex-center q-gutter-sm">
          <q-icon
            :name="symRoundedInbox"
            size="2em" />
          <span>{{ t('composites.TransactionProcessingDashboard.table.no_transactions') }}</span>
        </div>
      </template>
    </q-table>
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedCheckCircle,
  symRoundedClose,
  symRoundedContentCopy,
  symRoundedEdit,
  symRoundedError,
  symRoundedFileDownloadOff,
  symRoundedInbox,
  symRoundedMoreVert,
  symRoundedRefresh,
  symRoundedReplay,
  symRoundedSchedule,
  symRoundedSync,
  symRoundedVisibility,
} from '@quasar/extras/material-symbols-rounded'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Transaction, TransactionStatus, TransactionTableEmits, TransactionTableProps, TransmissionStatus, TransmissionType } from '../types'

const props = withDefaults(defineProps<TransactionTableProps>(), {
  loading: false,
  selectable: false,
  dense: false,
})

const emit = defineEmits<TransactionTableEmits>()

const { t } = useI18n()

// Local state
const selected = ref<Transaction[]>([])
const pagination = ref({
  page: 1,
  rowsPerPage: 25,
  sortBy: 'created_at',
  descending: true,
})

// Table columns configuration
const columns = computed(() => [
  {
    name: 'id',
    label: t('composites.TransactionProcessingDashboard.table.columns.id'),
    field: 'id',
    sortable: true,
    align: 'left' as const,
    style: 'width: 120px',
  },
  {
    name: 'status',
    label: t('composites.TransactionProcessingDashboard.table.columns.status'),
    field: 'status',
    sortable: true,
    align: 'center' as const,
    style: 'width: 150px',
  },
  {
    name: 'customer',
    label: t('composites.TransactionProcessingDashboard.table.columns.customer'),
    field: 'receiver_billing',
    sortable: false,
    align: 'left' as const,
    style: 'min-width: 200px',
  },
  {
    name: 'transaction_date',
    label: t('composites.TransactionProcessingDashboard.table.columns.date'),
    field: 'transaction_date',
    sortable: true,
    align: 'left' as const,
    style: 'width: 150px',
  },
  {
    name: 'total_amount',
    label: t('composites.TransactionProcessingDashboard.table.columns.amount'),
    field: 'amount_total',
    sortable: true,
    align: 'right' as const,
    style: 'width: 120px',
  },
  {
    name: 'transmissions',
    label: t('composites.TransactionProcessingDashboard.table.columns.transmissions'),
    field: 'transmissions',
    sortable: false,
    align: 'left' as const,
    style: 'min-width: 200px',
  },
  {
    name: 'actions',
    label: t('composites.TransactionProcessingDashboard.table.columns.actions'),
    field: 'actions',
    sortable: false,
    align: 'center' as const,
    style: 'width: 120px',
  },
])

/**
 * Get status color for badges
 */
const getStatusColor = (status: TransactionStatus): string => {
  switch (status) {
    case 'ready_to_invoice':
      return 'orange'
    case 'ready_to_send':
      return 'blue'
    case 'sent':
      return 'positive'
    case 'failed':
      return 'negative'
    case 'need_check':
      return 'warning'
    case 'cancelled':
      return 'grey'
    case 'processing':
      return 'info'
    default:
      return 'primary'
  }
}

/**
 * Get status label
 */
const getStatusLabel = (status: TransactionStatus): string => {
  return t(`crudmodels.transaction.status.${status}`)
}

/**
 * Get transmission status color
 */
const getTransmissionStatusColor = (status: TransmissionStatus): string => {
  switch (status) {
    case 'success':
      return 'positive'
    case 'failed':
      return 'negative'
    case 'pending':
      return 'warning'
    case 'processing':
      return 'info'
    case 'cancelled':
      return 'grey'
    default:
      return 'grey'
  }
}

/**
 * Get transmission status icon
 */
const getTransmissionStatusIcon = (status: TransmissionStatus): string => {
  switch (status) {
    case 'success':
      return symRoundedCheckCircle
    case 'failed':
      return symRoundedError
    case 'pending':
      return symRoundedSchedule
    case 'processing':
      return symRoundedSync
    default:
      return symRoundedSchedule
  }
}

/**
 * Get transmission type label
 */
const getTransmissionTypeLabel = (type: TransmissionType): string => {
  return t(`composites.TransactionProcessingDashboard.transmission_type.${type}_short`)
}

/**
 * Format currency amount
 */
const formatCurrency = (amount: number, currency?: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'CAD', // Default to CAD if no currency specified
  }).format(amount)
}

/**
 * Format date
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString()
}

/**
 * Format relative time
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

  if (diffInHours < 1) {
    return t('common.time.just_now')
  } else if (diffInHours < 24) {
    return t('common.time.hours_ago', { count: diffInHours })
  } else {
    const diffInDays = Math.floor(diffInHours / 24)
    return t('common.time.days_ago', { count: diffInDays })
  }
}

/**
 * Check if transaction has failed transmissions
 */
const hasFailedTransmissions = (transaction: Transaction): boolean => {
  return transaction.transmissions?.some((t) => t.status === 'failed') || false
}

/**
 * Handle row click
 */
const handleRowClick = (_event: Event, row: Transaction): void => {
  emit('row-click', row)
}
</script>

<style lang="scss" scoped>
.transaction-table {
  .q-table {
    .q-td {
      white-space: nowrap;
    }

    .q-badge {
      min-width: 80px;
    }
  }
}
</style>
